import { gachaEvmAddressSchema } from "@anaconda/core/gacha/evm-chains"
import type { User } from "@privy-io/react-auth"
import { wrap } from "@reatom/core"
import { z } from "zod"
import { getGachaEvmWalletClientType } from "./gacha-evm-wallet-connection"
import type { GachaEvmWalletProvider } from "./portfolio/gacha-evm-transfer"

const providerSchema = z.custom<GachaEvmWalletProvider>(
  (value) =>
    value !== null &&
    typeof value === "object" &&
    "request" in value &&
    typeof value.request === "function",
)
const announcementSchema = z.object({
  info: z.object({ rdns: z.string(), name: z.string() }),
  provider: providerSchema,
})
const legacyFlagsSchema = z.object({
  isMetaMask: z.boolean().optional(),
  isRabby: z.boolean().optional(),
  isBraveWallet: z.boolean().optional(),
  isCoinbaseWallet: z.boolean().optional(),
  isPhantom: z.boolean().optional(),
  isRainbow: z.boolean().optional(),
  isZerion: z.boolean().optional(),
  isOkxWallet: z.boolean().optional(),
})

function matchesLegacyProvider(
  provider: GachaEvmWalletProvider,
  clientType: string,
) {
  const result = legacyFlagsSchema.safeParse(provider)
  if (!result.success) return false
  const flags = result.data
  switch (clientType) {
    case "metamask":
      return flags.isMetaMask === true && !flags.isRabby && !flags.isBraveWallet
    case "rabby":
      return flags.isRabby === true
    case "coinbase":
      return flags.isCoinbaseWallet === true
    case "phantom":
      return flags.isPhantom === true
    case "rainbow":
      return flags.isRainbow === true
    case "zerion":
      return flags.isZerion === true
    case "okx":
      return flags.isOkxWallet === true
    default:
      return false
  }
}

export async function discoverGachaEvmWalletProvider(
  clientType: string,
  target: Window,
) {
  const announced: z.infer<typeof announcementSchema>[] = []
  const onAnnouncement = (event: Event) => {
    if (!(event instanceof CustomEvent)) return
    const result = announcementSchema.safeParse(event.detail)
    if (
      result.success &&
      !announced.some((entry) => entry.provider === result.data.provider)
    )
      announced.push(result.data)
  }
  target.addEventListener("eip6963:announceProvider", onAnnouncement)
  try {
    // Discovery is prompt-free. Never choose the first injected wallet: match the
    // client recorded for the linked address, even with several extensions installed.
    target.dispatchEvent(new Event("eip6963:requestProvider"))
    await wrap(new Promise<void>((resolve) => target.setTimeout(resolve, 100)))
  } finally {
    target.removeEventListener("eip6963:announceProvider", onAnnouncement)
  }
  const matching = announced.filter(
    ({ info }) =>
      info.rdns.toLowerCase().split(".").includes(clientType) ||
      info.name
        .toLowerCase()
        .replace(/[\s_-]/g, "")
        .replace(/wallet$|extension$/, "") === clientType.replace(/[_-]/g, ""),
  )
  if (matching.length > 0)
    return matching.length === 1 ? matching[0]?.provider : undefined

  const legacy = z
    .object({
      ethereum: providerSchema.optional(),
      phantom: z.object({ ethereum: providerSchema.optional() }).optional(),
    })
    .safeParse(target)
  if (!legacy.success) return undefined
  const providers = legacy.data.ethereum
    ? (z
        .object({ providers: z.array(providerSchema).optional() })
        .parse(legacy.data.ethereum).providers ?? [legacy.data.ethereum])
    : []
  if (legacy.data.phantom?.ethereum)
    providers.push(legacy.data.phantom.ethereum)
  const candidates = [...new Set(providers)].filter((provider) =>
    matchesLegacyProvider(provider, clientType),
  )
  return candidates.length === 1 ? candidates[0] : undefined
}

export async function reconnectGachaEvmWallet(
  linkedAccounts: User["linkedAccounts"] | undefined,
  address: string,
  target: Window,
): Promise<"connected" | "unlinked" | "unavailable" | "mismatch"> {
  const clientType = getGachaEvmWalletClientType(linkedAccounts, address)
  if (!clientType) return "unlinked"
  const provider = await wrap(
    discoverGachaEvmWalletProvider(clientType, target),
  )
  if (!provider) return "unavailable"
  const accounts = z
    .array(gachaEvmAddressSchema)
    .parse(await wrap(provider.request({ method: "eth_requestAccounts" })))
  if (
    !accounts.some((account) => account.toLowerCase() === address.toLowerCase())
  )
    return "mismatch"
  const exposed = z
    .array(gachaEvmAddressSchema)
    .parse(await wrap(provider.request({ method: "eth_accounts" })))
  return exposed.some(
    (account) => account.toLowerCase() === address.toLowerCase(),
  )
    ? "connected"
    : "mismatch"
}
